import React, { Dispatch, SetStateAction } from 'react';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { TableCell, TableRow } from '../../../../CommonComponents/OPL/Table/Table';
import { useManageAccounts } from './useManageAccounts';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Lozenge } from '../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { ModalWrapper } from './components/ModalWrapper/ModalWrapper';
import { AchRelationshipDTO, StatusDTO } from '../../api/useAchRelationshipsQuery';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { ModalType } from '../TransferModal/TransferModal.types';
import { EmptyManageAccounts } from './ManageAccounts.empty';
import { LozengeColor } from '../../../../CommonComponents/OPL/Lozenge/Lozenge.styles';
import { Tooltip } from '../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Loading } from '../../../../CommonComponents/OPL/Loading/Loading';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';

const STATUS_COLOR_MAP: Record<StatusDTO, LozengeColor> = {
  [StatusDTO.APPROVED]: 'success',
  [StatusDTO.PENDING]: 'warning',
  [StatusDTO.REJECTED]: 'error',
};

interface Props {
  onBack(): void;
  setActiveModal: Dispatch<SetStateAction<ModalType | null>>;
  setSelectedAccount: Dispatch<SetStateAction<AchRelationshipDTO | null>>;
}

export const ManageAccounts: React.FC<Props> = ({ onBack, setActiveModal, setSelectedAccount }) => {
  const { isLoading, isError, refetch, data } = useManageAccounts();

  if (isLoading) {
    return (
      <Box display="grid" style={{ placeItems: 'center', minHeight: '30vh' }}>
        <Loading />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="grid" style={{ placeItems: 'center', minHeight: '30vh' }}>
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const openLinkAccountModal = () => setActiveModal(ModalType.SelectAccount);

  const { relationships, maxRelationships } = data;

  if (relationships.length === 0) {
    return (
      <ModalWrapper canLinkAccount openLinkAccountModal={openLinkAccountModal} onBack={onBack}>
        <EmptyManageAccounts />
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      openLinkAccountModal={openLinkAccountModal}
      onBack={onBack}
      canLinkAccount={
        relationships.filter(
          account => account.status === StatusDTO.APPROVED || account.status === StatusDTO.PENDING,
        ).length < maxRelationships
      }
    >
      {relationships.map(account => (
        <TableRow px="24" key={account.id}>
          <TableCell width="60%">
            <Box display="flex" flexDirection="column">
              <Text fontWeight={500} color="neutral.700">
                {account.name}
              </Text>
              <HelperText>{account.institutionName}</HelperText>
            </Box>
          </TableCell>

          <TableCell width="25%" justifyContent="flex-end">
            <Tooltip
              disabled={account.rejectionReasons.length === 0}
              content={
                <>
                  {account.rejectionReasons.map(rejectionReason => (
                    <Text
                      key={rejectionReason}
                      isOverflowWrap
                      fontWeight={600}
                      fontSize="small"
                      color="white"
                    >
                      {rejectionReason}
                    </Text>
                  ))}
                </>
              }
            >
              <span>
                <Lozenge
                  variantColor={STATUS_COLOR_MAP[account.status] || 'default'}
                  icon={
                    account.status === StatusDTO.REJECTED ? 'far fa-exclamation-circle' : undefined
                  }
                >
                  {account.status}
                </Lozenge>
              </span>
            </Tooltip>
          </TableCell>

          <TableCell width="15%" justifyContent="center">
            <IconButton
              aria-label="Delete account"
              variant="subtleInactive"
              icon="far fa-trash-alt"
              onClick={() => {
                setActiveModal(ModalType.DeleteAccount);
                setSelectedAccount(account);
              }}
            />
          </TableCell>
        </TableRow>
      ))}
    </ModalWrapper>
  );
};
